import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Copy, Check, QrCode, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { logError } from "@/lib/log";

export default function DonationQR() {
  const canvasWrapRef = useRef(null);
  const [donateUrl, setDonateUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setDonateUrl(`${window.location.origin}/donate`);
    }
  }, []);

  const downloadPng = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "lavish808-donate-qr.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(donateUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      logError("[DonationQR] clipboard copy failed:", err);
    }
  };

  return (
    <section className="py-16" data-testid="donate-qr-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center rounded-3xl p-8 md:p-12"
          style={{ backgroundColor: "#F7ECD9" }}
        >
          <div className="md:col-span-5 flex justify-center">
            <div
              ref={canvasWrapRef}
              className="bg-white p-5 rounded-2xl shadow-md"
              style={{ border: "2px solid #0E5C6E" }}
              data-testid="donate-qr-canvas"
            >
              {donateUrl && (
                <QRCodeCanvas
                  value={donateUrl}
                  size={224}
                  bgColor="#FFFFFF"
                  fgColor="#0E5C6E"
                  level="H"
                  includeMargin={false}
                />
              )}
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#E27E4E" }}>
              <QrCode size={14} /> Scan • Share • Kōkua
            </div>
            <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: "#0E5C6E" }}>
              Scan to donate with Aloha.
            </h2>
            <p className="leading-relaxed mb-6" style={{ color: "#2D2B2A" }}>
              Print this QR on flyers, business cards, or event signs. Anyone who scans it
              lands right on our donation page — no fees, no fuss, just kōkua for the pups.
            </p>

            <div className="flex flex-wrap gap-3">
              <button onClick={downloadPng} className="btn-donate" data-testid="donate-qr-download">
                <Download size={16} /> Download QR (PNG)
              </button>
              <button
                onClick={copyLink}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium border transition-colors"
                style={{ borderColor: "#0E5C6E", color: "#0E5C6E", backgroundColor: "white" }}
                data-testid="donate-qr-copy"
              >
                {copied ? <><Check size={16} /> Link copied</> : <><Copy size={16} /> Copy link</>}
              </button>
              <Link
                to="/qr-flyer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium border transition-colors"
                style={{ borderColor: "#E27E4E", color: "#E27E4E", backgroundColor: "white" }}
                data-testid="donate-qr-flyer-link"
              >
                <Printer size={16} /> Print flyer
              </Link>
            </div>

            <div className="mt-4 text-xs break-all" style={{ color: "#6B655E" }} data-testid="donate-qr-url">
              {donateUrl}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
